import { Component, OnInit, OnDestroy, AfterViewInit, Inject, Input, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HTTPService } from 'src/app/services/http.service';
import { UntypedFormGroup } from '@angular/forms';
import { BehaviorSubject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { DynamicFormsService } from 'src/app/services/dynamic-forms.service';
import { columnsDef } from 'src/app/interfaces/columnsDef';
import { UserAuthenticationServices } from 'src/app/services/user-authentication.services';


interface Field {
  value: string;
  viewValue: string;
  dataType: string;
}

interface SearchTableColumn {
  prop: string;
  name: string;
  width: number;
  isDate: boolean;
}

@Component({
    selector: 'app-search-dialog',
    templateUrl: './search-dialog.component.html',
    styleUrls: ['./search-dialog.component.css'],
    standalone: false
})
export class SearchDialogComponent implements OnInit, OnDestroy, AfterViewInit {

  @Input("entityName") entityName ="sem nome";
  @Input("entityDesc") entityDesc = "Sem descrição";
  @Input("customCondition") customCondition;

  field: Field[] = [];
  tableColumns: SearchTableColumn[] = [];
  rows: Object[] = [];
  displayedColumns: columnsDef[] = [];

  loading = true;
  /** null = pesquisar em todos os campos */
  selectedColumn: string = null;
  searchText = '';
  inputType = 'text';
  selectedRow: Object = null;

  private group: UntypedFormGroup;
  private configRef: columnsDef;
  private component: DynamicFormsService;
  private sub: Subscription;
  private sub2: Subscription;
  private sub4: Subscription;

  private subject: BehaviorSubject<string> = new BehaviorSubject(null);

  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  constructor(@Inject(MAT_DIALOG_DATA) public data, public dialogRef:MatDialogRef<SearchDialogComponent>, private httpService:HTTPService, public cd: ChangeDetectorRef,
  public uas:UserAuthenticationServices) {
    this.entityName = data.entityName;
    this.entityDesc = data.entityDesc;
    this.group = data.group;
    this.configRef = data.configRef;
    this.component = data.component;
    this.customCondition = data.customCondition;

    this.sub4 = this.subject.pipe(debounceTime(600)).subscribe(searchTextValue => {
      if ( searchTextValue == null ){
        return;
      }
      this.doSearch(searchTextValue);
    });
  }

  ngOnInit() {
    this.doSearch('');
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.searchInput?.nativeElement?.focus());
  }

  ngOnDestroy(){
    if (this.sub!= null ) {this.sub.unsubscribe();}
    if (this.sub2!= null ) {this.sub2.unsubscribe();}
    if (this.sub4!= null ) {this.sub4.unsubscribe();}
  }

  /** monta a condição de pesquisa: coluna específica ou OR entre os campos visíveis */
  private buildCondition(term: string): { condition: string, parameters?: any[] } {
    if ( term == null || term.trim() === '' ){
      return { condition: null };
    }

    term = term.trim();

    if ( this.selectedColumn != null ){
      const dataType = this.displayedColumns.find(x => x.COLUMNNAME == this.selectedColumn)?.DATATYPE;
      if ( dataType == 'VARCHAR' ){
        return { condition: `this.${this.selectedColumn} like '%${term}%'` };
      }
      return { condition: `this.${this.selectedColumn} =:${this.selectedColumn}`, parameters: [term] };
    }

    const parts: string[] = [];
    const isNumeric = !isNaN(Number(term)) && term !== '';

    this.field.forEach(f => {
      if ( f.dataType == 'VARCHAR' ){
        parts.push(`this.${f.value} like '%${term}%'`);
      } else if ( isNumeric && (f.dataType == 'INT' || f.dataType == 'SMALLINT' || f.dataType == 'FLOAT' || f.dataType == 'SHORT') ){
        parts.push(`this.${f.value} = ${Number(term)}`);
      }
    });

    if ( parts.length === 0 ){
      return { condition: null };
    }
    return { condition: '(' + parts.join(' OR ') + ')' };
  }

  private doSearch(term: string){
    this.loading = true;
    this.selectedRow = null;

    let ob: Object = {
      entityName: this.entityName,
      showPresentation: true,
      showRelationship: false,
      rows: 50
    };

    const built = this.buildCondition(term);
    if ( built.condition != null ){
      ob['condition'] = built.condition;
      if ( built.parameters != null ){
        ob['parameters'] = built.parameters;
      }
    }

    if ( this.customCondition ){
      if ( ob['condition'] != null && ob['condition'].length > 0 ){
        ob['condition'] = this.customCondition + ' and ' + ob['condition'];
      } else {
        ob['condition'] = this.customCondition;
      }
    }

    this.sub2 = this.httpService.post(ob, "JsonBrokerServices/find", false).subscribe(
      dataInfo => {
        if ( this.displayedColumns == null || this.displayedColumns.length === 0 ){
          this.displayedColumns = dataInfo['table_information'];
          this.mountColumns();
        }
        this.rows = [...dataInfo[this.entityName.toString()]];
        this.loading = false;
        this.cd.markForCheck();
      },
      (error:any) => {
        this.loading = false;
        throw error;
      }
    );
  }

  mountColumns(){
    this.displayedColumns
      .filter(x => x.SEARCHVISIBLE == "S" || (x.COLUMNNAME.indexOf("->") > -1 && x.VALIDATIONERROR != null))
      .forEach((c: columnsDef) => {
        this.tableColumns.push({
          prop: c.COLUMNNAME,
          name: c.COLUMNDESC,
          width: c.COLWIDTH == null || c.COLWIDTH == 0 ? 150 : c.COLWIDTH,
          isDate: c.DATATYPE == 'DATETIME' || c.DATATYPE == 'DATE'
        });
        this.field.push({ value: c.COLUMNNAME, viewValue: c.COLUMNDESC, dataType: String(c.DATATYPE) });
      });
  }

  onSearchChange(value: string){
    this.subject.next(value == null ? '' : value);
  }

  onScopeChange(value: string){
    if ( value != null ){
      const fieldF = this.field.find(x => x.value == value);
      if ( fieldF != null ){
        if ( fieldF.dataType == 'INT' || fieldF.dataType == 'SMALLINT' || fieldF.dataType == 'SHORT' ){
          this.inputType = 'number';
        } else if ( fieldF.dataType == 'DATETIME' || fieldF.dataType == 'DATE' ){
          this.inputType = 'date';
        } else {
          this.inputType = 'text';
        }
      }
    } else {
      this.inputType = 'text';
    }

    // reaplica a pesquisa com o termo atual no novo escopo
    this.subject.next(this.searchText == null ? '' : this.searchText);
    setTimeout(() => this.searchInput?.nativeElement?.focus());
  }

  selectRow(row: Object){
    this.selectedRow = row;
  }

  confirm(row?: Object){
    if ( row != null ){
      this.selectedRow = row;
    }

    if ( this.selectedRow == null ){
      return;
    }

    this.component.editingEventHandle(null);
    this.closeDialog(this.selectedRow);
  }

  closeDialog(data){
    if ( data != null){
      this.dialogRef.close(data);
    }else{
      this.dialogRef.close(false);
    }
  }
}
